import { useState, type FormEvent } from 'react';
import {
  EMAIL_MAX,
  EMAIL_PATTERN,
  EMPLOYEE_ID_MAX,
  EMPLOYEE_ID_PATTERN,
  PASSWORD_MAX,
  PASSWORD_MIN,
  type CreateUserRequest,
  type EmailStatus,
  type UpdateUserRequest,
  type UserSummary,
  type ValidationDetails,
} from '@todo/shared';
import { ApiError } from '../api/client';
import { userApi } from '../api/user.api';
import { useAuth } from '../hooks/useAuth';
import { AvatarPicker } from './AvatarPicker';
import { DepartmentSelect } from './DepartmentSelect';
import { RoleSelect } from './RoleSelect';

/**
 * PP-03 - add a person.
 *
 * The account is created INVITED with a password the administrator chooses;
 * the first successful sign-in promotes it to ACTIVE. The password is then
 * EMAILED to the person as a welcome mail, and `welcomeMail` on the create
 * response says whether that worked - when it did not, the administrator has
 * to hand the password over themselves, because there is still no
 * self-service sign-up and no self-service password reset.
 *
 * The BANNER for that is currently commented out - see the note at the
 * welcomeMail check in submit().
 *
 * Validation happens twice on purpose. Here, so a typo is caught without a
 * round trip; and in the API, because a browser check is a convenience, never
 * a control. The rules come from @todo/shared so both copies cannot drift.
 */

interface UserFormProps {
  /** Present = EDIT mode: prefill from this person and PATCH instead of POST.
   *  Username and password disappear - the first is fixed identity, the
   *  second has its own reset flow. */
  user?: UserSummary;
  /** `warning` is set when the account was saved but the photo did not upload. */
  onCreated: (user: UserSummary, warning?: string) => void;
  onCancel: () => void;
}

interface FormState {
  employeeId: string;
  username: string;
  password: string;
  displayName: string;
  email: string;
  jobTitle: string;
}

const EMPTY: FormState = {
  employeeId: '',
  username: '',
  password: '',
  displayName: '',
  email: '',
  jobTitle: '',
};

/** Mirrors the API's Zod schema and the database CHECK constraints.
 *  `linked` = the email belongs to an existing account in another org, so
 *  no password is collected (they keep the one they already have). */
function validate(
  form: FormState,
  editing: boolean,
  linked: boolean,
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!editing && !linked) {
    if (form.password.length < PASSWORD_MIN || form.password.length > PASSWORD_MAX) {
      errors.password = `Between ${PASSWORD_MIN} and ${PASSWORD_MAX} characters`;
    }
  }

  if (!form.displayName.trim()) {
    errors.displayName = 'Full name is required';
  }

  // Email is MANDATORY: it is unique within the organisation and becomes the
  // person's sign-in identity under the unified login.
  const email = form.email.trim();
  if (!email) {
    errors.email = 'Email is required';
  } else if (email.length > EMAIL_MAX) {
    errors.email = `Must be ${EMAIL_MAX} characters or fewer`;
  } else if (!EMAIL_PATTERN.test(email)) {
    errors.email = 'Enter a valid email address';
  }

  // Optional, but must be well formed when supplied. An empty string is NOT
  // the same as absent: it would collide with the next blank one in the
  // unique index, so it is sent as undefined rather than ''.
  const employeeId = form.employeeId.trim();
  if (employeeId) {
    if (employeeId.length > EMPLOYEE_ID_MAX) {
      errors.employeeId = `Must be ${EMPLOYEE_ID_MAX} characters or fewer`;
    } else if (!EMPLOYEE_ID_PATTERN.test(employeeId)) {
      errors.employeeId = 'Letters, numbers, hyphen, underscore or slash only';
    }
  }

  return errors;
}

/** Initials for the photo placeholder - the same fallback the list uses. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function UserForm({ user, onCreated, onCancel }: UserFormProps) {
  const editing = user !== undefined;
  const { user: me } = useAuth();
  /** Only a Super Admin may hand out the Super Admin role - matches the
   *  server-side check in user.service.ts, which is the actual control. */
  const hiddenRoleKeys = me?.role.systemKey === 'SUPER_ADMIN' ? [] : ['SUPER_ADMIN'];
  const [form, setForm] = useState<FormState>(
    user
      ? {
          employeeId: user.employeeId ?? '',
          username: user.username,
          password: '',
          displayName: user.displayName,
          email: user.email ?? '',
          jobTitle: user.jobTitle ?? '',
        }
      : EMPTY,
  );
  const [photo, setPhoto] = useState<File | null>(null);
  /** undefined = let the API apply the organisation's default role. */
  const [roleId, setRoleId] = useState<string | undefined>(user?.roleId);
  const [departmentId, setDepartmentId] = useState<string | undefined>(
    user?.departmentId ?? undefined,
  );
  /**
   * The email pre-flight ('free' | 'in-this-org' | 'exists-elsewhere'),
   * refreshed when the email field loses focus. 'exists-elsewhere' switches
   * the form into LINK mode: no password is collected, and the person is
   * added to this organisation under their existing login. The server
   * re-checks at create time regardless - this state is UX, not the control.
   */
  const [emailStatus, setEmailStatus] = useState<EmailStatus | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function update<K extends keyof FormState>(key: K, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
    // An edited email invalidates the last check until the next blur.
    if (key === 'email') setEmailStatus(null);
    // Clear this field's error as soon as it is touched; re-validating the
    // whole form on every keystroke turns an empty form into a wall of red.
    setErrors((current) => {
      if (!current[key]) return current;
      const { [key]: _removed, ...rest } = current;
      return rest;
    });
  }

  /** onBlur of the email field: ask the API what creating it would do. */
  async function checkEmail() {
    const email = form.email.trim().toLowerCase();
    if (editing || !email || !EMAIL_PATTERN.test(email)) return;
    try {
      const { status } = await userApi.emailStatus(email);
      setEmailStatus(status);
      if (status === 'in-this-org') {
        setErrors((current) => ({
          ...current,
          email: 'This email is already used by someone in this organisation',
        }));
      }
    } catch {
      // The check is a convenience - the server re-validates on submit.
      setEmailStatus(null);
    }
  }

  const linked = emailStatus === 'exists-elsewhere';

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    if (emailStatus === 'in-this-org') {
      setErrors({ email: 'This email is already used by someone in this organisation' });
      return;
    }

    const clientErrors = validate(form, editing, linked);
    // Department is mandatory - an admin's whole view is fenced by it.
    if (!departmentId) clientErrors.departmentId = 'Choose a department';
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    setSubmitting(true);

    let saved: UserSummary;
    /* Only the CREATE path reports on a welcome mail; an edit sends none. */
    let mailWarning: string | null = null;

    try {
      if (editing && user) {
        const body: UpdateUserRequest = {
          ...(form.employeeId.trim() ? { employeeId: form.employeeId.trim() } : {}),
          displayName: form.displayName.trim(),
          email: form.email.trim().toLowerCase(),
          ...(form.jobTitle.trim() ? { jobTitle: form.jobTitle.trim() } : {}),
          ...(roleId && roleId !== user.roleId ? { roleId } : {}),
          ...(departmentId && departmentId !== (user.departmentId ?? undefined)
            ? { departmentId } : {}),
        };
        saved = await userApi.update(user.id, body);
      } else {
        // No username: the API derives one from the email. No password in
        // LINK mode: the person keeps the one their account already has.
        const body: CreateUserRequest = {
          ...(form.employeeId.trim() ? { employeeId: form.employeeId.trim() } : {}),
          ...(linked ? {} : { password: form.password }),
          displayName: form.displayName.trim(),
          email: form.email.trim().toLowerCase(),
          ...(form.jobTitle.trim() ? { jobTitle: form.jobTitle.trim() } : {}),
          ...(roleId ? { roleId } : {}),
          ...(departmentId ? { departmentId } : {}),
        };
        const result = await userApi.create(body);
        saved = result;
        /*
         * MAIL NOTICE SILENCED 2026-10-09 - UNCOMMENT TO RESTORE.
         *
         * The sending mailbox is throttled by Exchange Online (450
         * 4.5.127), so welcomeMail came back 'failed' on EVERY creation
         * and the warning stopped carrying information - it fired whether
         * or not anything was wrong with that particular person.
         *
         * The reason it existed still stands: there is no self-service
         * password reset here, so someone who never got their credentials
         * cannot get in without the administrator stepping in again. What
         * makes silencing it tolerable for now is that the administrator
         * TYPES the temporary password on this form, so they already hold
         * it - the plain "added" message tells them to pass it on.
         *
         * RESTORE THIS once mail sends again, or the day a welcome mail
         * fails for one person in a working system will pass unnoticed.
         * `welcomeMail` is still on the response; only the banner is off.
         *
         * 'skipped' was kept separate from 'failed' on purpose: skipped
         * means mail is switched off or that address is deliberately
         * muted, which is a setting, not a fault to go hunting for.
         */
        // if (result.welcomeMail === 'failed') {
        //   mailWarning = `${saved.displayName} was created, but the welcome email could not be sent.`
        //     + ' Pass the sign-in details on yourself.';
        // } else if (result.welcomeMail === 'skipped') {
        //   mailWarning = `${saved.displayName} was created. No welcome email was sent`
        //     + ' (email is switched off or this address is muted), so pass the'
        //     + ' sign-in details on yourself.';
        // }
        void result.welcomeMail; // keep the field read while the banner is off
      }
    } catch (error) {
      applyError(error);
      setSubmitting(false);
      return;
    }

    /**
     * The photo goes up SECOND, and its failure does not fail the form.
     *
     * The blob key is avatars/<userId>/<version>, so the account has to exist
     * first. Rolling the account back because an image did not upload would be
     * the wrong trade: the person can still sign in without a photo, and the
     * admin would otherwise retype everything.
     */
    if (photo) {
      try {
        await userApi.setAvatar(saved.id, photo);
      } catch {
        onCreated(saved, [
          mailWarning,
          `${saved.displayName} was saved, but the photo did not upload. You can add it later.`,
        ].filter(Boolean).join(' '));
        setSubmitting(false);
        return;
      }
    }

    onCreated(saved, mailWarning ?? undefined);
    setSubmitting(false);
  }

  /** Map the API's error envelope onto the fields it names. */
  function applyError(error: unknown) {
    if (!(error instanceof ApiError)) {
      setFormError('Something went wrong. Please try again.');
      return;
    }

    if (error.code === 'CONFLICT') {
      // A 409 names the email or the employee id - put the message under
      // that field; anything else (e.g. a rare username race) goes to the
      // banner, since there is no username field to attach it to any more.
      const lower = error.message.toLowerCase();
      if (lower.includes('email')) {
        setErrors({ email: error.message });
      } else if (lower.includes('employee')) {
        setErrors({ employeeId: error.message });
      } else {
        setFormError(error.message);
      }
      return;
    }

    if (error.code === 'VALIDATION_ERROR' && error.details) {
      // The API prefixes keys with the source: "body.username".
      const details = error.details as ValidationDetails;
      const mapped: Record<string, string> = {};
      for (const [key, messages] of Object.entries(details)) {
        mapped[key.replace(/^body\./, '')] = messages[0] ?? 'Invalid value';
      }
      setErrors(mapped);
      return;
    }

    setFormError(error.message);
  }

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col">
      <div className="flex-1 space-y-5 px-6 py-5">
        {formError && (
          <p
            role="alert"
            className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-100"
          >
            {formError}
          </p>
        )}

        <AvatarPicker
          file={photo}
          onChange={setPhoto}
          fallback={initials(form.displayName)}
          disabled={submitting}
        />

        <Field
          label="Employee ID"
          value={form.employeeId}
          onChange={(v) => update('employeeId', v)}
          error={errors.employeeId}
          placeholder="CT-0042"
          hint="Your company's own number for this person. Must be unique."
        />

        <Field
          label="Full name"
          value={form.displayName}
          onChange={(v) => update('displayName', v)}
          error={errors.displayName}
          placeholder="Priya Raman"
          autoFocus
          required
        />

        <Field
          label="Email"
          type="email"
          value={form.email}
          onChange={(v) => update('email', v)}
          onBlur={() => void checkEmail()}
          error={errors.email}
          placeholder="priya.raman@charles-technologies.com"
          hint="Signs them in everywhere. Unique in this organisation."
          required
        />

        {!editing && linked && (
          <p
            role="status"
            className="rounded-lg bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-800 ring-1 ring-amber-200"
          >
            This person already exists in another organisation. They will be
            added to your organisation and can sign in with the{' '}
            <strong>same email and password</strong> they already use - no new
            password is needed.
          </p>
        )}

        {!editing && !linked && (
          <Field
            label="Temporary password"
            type="password"
            revealable
            value={form.password}
            onChange={(v) => update('password', v)}
            error={errors.password}
            hint="Emailed to them as a welcome message. There is no self-service reset, so you will be told if it could not be sent."
            required
          />
        )}

        <RoleSelect
          value={roleId}
          onChange={setRoleId}
          error={errors.roleId}
          disabled={submitting}
          hiddenSystemKeys={hiddenRoleKeys}
        />

        <DepartmentSelect
          required
          value={departmentId}
          onChange={setDepartmentId}
          error={errors.departmentId}
          disabled={submitting}
        />

        <Field
          label="Job title"
          value={form.jobTitle}
          onChange={(v) => update('jobTitle', v)}
          error={errors.jobTitle}
          placeholder="Site Engineer"
        />

        {!editing && !linked && (
          <p className="rounded-lg bg-slate-50 px-3 py-2.5 text-xs leading-relaxed text-slate-500 ring-1 ring-slate-200">
            The account stays <strong>Invited</strong> until the first sign-in,
            then becomes Active automatically.
          </p>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg px-3.5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting
            ? (photo ? 'Uploading...' : 'Saving...')
            : editing ? 'Save changes' : 'Create person'}
        </button>
      </div>
    </form>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  hint?: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
  autoFocus?: boolean;
  /** password fields only: adds a Show / Hide toggle inside the input. */
  revealable?: boolean;
}

function Field({
  label,
  value,
  onChange,
  onBlur,
  error,
  hint,
  type = 'text',
  placeholder,
  required,
  autoFocus,
  revealable,
}: FieldProps) {
  const id = `field-${label.toLowerCase().replace(/\s+/g, '-')}`;
  /** Show/Hide for password fields. Local to the field: closing or
   *  re-rendering the form goes back to hidden. */
  const [revealed, setRevealed] = useState(false);
  const inputType = revealable && revealed ? 'text' : type;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
        {!required && <span className="ml-1 text-slate-400">(optional)</span>}
      </label>
      <div className="relative">
      <input
        id={id}
        type={inputType}
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        className={[
          'mt-1.5 w-full rounded-lg border px-3 py-2 text-sm text-slate-900 outline-none transition',
          'placeholder:text-slate-400 focus:ring-2',
          revealable ? 'pr-14' : '',
          error
            ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
            : 'border-slate-300 focus:border-brand-500 focus:ring-brand-100',
        ].join(' ')}
      />
      {revealable && (
        <button
          type="button"
          onClick={() => setRevealed((v) => !v)}
          aria-label={revealed ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
          aria-pressed={revealed}
          className="absolute inset-y-0 right-2 top-1.5 my-auto h-7 rounded-md px-2 text-xs font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
        >
          {revealed ? 'Hide' : 'Show'}
        </button>
      )}
      </div>
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-xs text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1 text-xs text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
